// Hex ↔ HSL, the two conversions the app's colour work is written in.
//
// Saturation and lightness are the axes the questions are actually about —
// "make this pastel read on a dark card", "take this red out of the white" —
// but a colour is stored and rendered as a hex, so the work is a round trip.
// `src/lib/constants.ts` is the caller: `saturateForDark` and the priority-flag
// palette both lift a stored hex in HSL and hand a hex back.
//
// These lived in `src/lib/brandTheme.ts` until the per-tenant repaint was
// removed; the conversions outlived it, so they moved here rather than going
// with it.

export interface Hsl {
  /** Hue in degrees, 0–360. */
  h: number;
  /** Saturation percentage, 0–100. */
  s: number;
  /** Lightness percentage, 0–100. */
  l: number;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const HEX = /^#([0-9a-f]{6})$/i;

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/** `#rrggbb` → HSL, or null when the string is not a six-digit hex colour. */
export function hexToHsl(hex: string | null | undefined): Hsl | null {
  if (typeof hex !== "string") return null;
  const match = HEX.exec(hex.trim());
  if (!match) return null;

  const int = parseInt(match[1], 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  const l = (max + min) / 2;

  if (d === 0) return { h: 0, s: 0, l: l * 100 };

  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
  else if (max === g) h = ((b - r) / d + 2) * 60;
  else h = ((r - g) / d + 4) * 60;

  return { h, s: s * 100, l: l * 100 };
}

function hslToRgb({ h, s, l }: Hsl): Rgb {
  const sat = s / 100;
  const lig = l / 100;
  const c = (1 - Math.abs(2 * lig - 1)) * sat;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const m = lig - c / 2;
  const [r, g, b] = (
    [
      [c, x, 0],
      [x, c, 0],
      [0, c, x],
      [0, x, c],
      [x, 0, c],
      [c, 0, x],
    ] as const
  )[Math.floor(hp) % 6];
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}

/**
 * HSL → `#rrggbb`, the inverse of `hexToHsl`.
 *
 * Rounds to 8 bits, so a colour that makes the round trip comes back to itself
 * rather than to a neighbour a fraction of a channel away.
 */
export function hslToHex(hsl: Hsl): string {
  const { r, g, b } = hslToRgb(hsl);
  return (
    "#" +
    [r, g, b]
      .map((v) =>
        Math.round(clamp(v, 0, 255))
          .toString(16)
          .padStart(2, "0"),
      )
      .join("")
  );
}
